// pages/admin/AdminAuditLog.jsx — journal des actions sensibles (impersonation,
// remboursements, suppressions de compte/client, changements de prix,
// modifications sensibles de réservation). Scopé au tenant de l'appelant.
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../utils/auth';
import { auditLogApi } from '../../utils/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ShieldAlert, ChevronDown, ChevronUp } from 'lucide-react';

const ACTION_META = {
  impersonation_start:              { label: 'Connexion en tant qu\'utilisateur', color: 'var(--red)' },
  refund_approved:                  { label: 'Remboursement approuvé',            color: 'var(--red)' },
  refund_rejected:                  { label: 'Remboursement rejeté',              color: 'var(--text-3)' },
  customer_deleted:                 { label: 'Client supprimé',                   color: 'var(--red)' },
  user_deactivated:                 { label: 'Compte utilisateur désactivé',      color: 'var(--red)' },
  price_profile_updated:            { label: 'Profil tarifaire modifié',          color: 'var(--amber)' },
  price_profile_deleted:            { label: 'Profil tarifaire supprimé',         color: 'var(--red)' },
  price_tiers_updated:              { label: 'Tranches de tarifs modifiées',      color: 'var(--amber)' },
  booking_amount_or_status_changed: { label: 'Montant/statut réservation modifié', color: 'var(--amber)' },
  booking_cancelled:                { label: 'Réservation annulée',               color: 'var(--red)' },
  manual_discount_applied:          { label: 'Remise manuelle/partenaire appliquée', color: 'var(--amber)' },
  manual_discount_removed:          { label: 'Remise manuelle/partenaire retirée', color: 'var(--text-3)' },
};

const FILTERS = [
  { value: '',                                  label: 'Tout' },
  { value: 'impersonation_start',                label: 'Impersonation' },
  { value: 'refund_approved',                    label: 'Remboursements' },
  { value: 'customer_deleted',                   label: 'Suppressions client' },
  { value: 'user_deactivated',                   label: 'Comptes désactivés' },
  { value: 'price_profile_updated',              label: 'Tarifs' },
  { value: 'booking_amount_or_status_changed',   label: 'Réservations modifiées' },
  { value: 'manual_discount_applied',            label: 'Remises manuelles/partenaires' },
];

function DetailRow({ entry }) {
  const [open, setOpen] = useState(false);
  const meta = ACTION_META[entry.action] || { label: entry.action, color: 'var(--text-3)' };
  const hasDetail = entry.detail && Object.keys(entry.detail).length > 0;

  return (
    <div className="card" style={{ padding: '12px 16px', marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: hasDetail ? 'pointer' : 'default' }}
        onClick={() => hasDetail && setOpen(o => !o)}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13.5 }}>{meta.label}</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
            {entry.actor_email || (entry.actor_type === 'superadmin' ? 'SuperAdmin' : 'Système')}
            {entry.target_type ? ` · ${entry.target_type}` : ''}
          </div>
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
          {format(new Date(entry.created_at), 'd MMM yyyy HH:mm', { locale: fr })}
        </div>
        {hasDetail && (open ? <ChevronUp size={14} color="var(--text-3)" /> : <ChevronDown size={14} color="var(--text-3)" />)}
      </div>
      {open && hasDetail && (
        <pre style={{ marginTop: 10, padding: '8px 12px', background: 'var(--bg-elevated)', borderRadius: 8,
          fontSize: 11.5, color: 'var(--text-2)', overflowX: 'auto' }}>
          {JSON.stringify(entry.detail, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default function AdminAuditLog() {
  const { can } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    auditLogApi.list(filter ? { action: filter } : {})
      .then(r => setEntries(r.data.entries || []))
      .finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  if (!can('admin')) return (
    <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>
  );

  return (
    <>
      <div className="page-header">
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldAlert size={20} /> Journal d'audit
          </h2>
          <p>Historique des actions sensibles — utile en cas de litige ou d'erreur à tracer</p>
        </div>
      </div>

      <div className="page-body">
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          {FILTERS.map(f => {
            const active = filter === f.value;
            return (
              <button key={f.value} onClick={() => setFilter(f.value)}
                style={{
                  padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                  border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                  background: active ? 'rgba(124,92,252,0.15)' : 'var(--bg-elevated)',
                  color: active ? 'var(--accent-light)' : 'var(--text-3)',
                  fontSize: 12, fontWeight: 700,
                }}>
                {f.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 30 }}><span className="spinner" /></div>
        ) : entries.length === 0 ? (
          <div className="empty-state card"><p>Aucune action enregistrée pour le moment</p></div>
        ) : (
          entries.map(entry => <DetailRow key={entry.id} entry={entry} />)
        )}
      </div>
    </>
  );
}
